import React, { useMemo, useState } from 'react';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import BeaconDoubleDropdownMetricColumnChart from 'src/components/BeaconV2Components/BeaconMetricColumnChart/BeaconDoubleDropdownMetricColumnChart';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useCreateRemoveRetailPriceRangeFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { useConvertPaidTrafficFieldName } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/PaidSearch/paidSearchUtil';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { shouldShowBeaconProTopics } from 'src/utils/app';
import { useMainTimePeriodShortDisplayName, useComparisonTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { useMainEntityType, useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { usePaidSearchMetric } from './PaidSearchMetricContext';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const fields = [
  {
    fieldName: 'spend',
    label: 'Ad Spend'
  },
  {
    fieldName: 'clicks',
    label: 'Ad Clicks'
  },
  {
    fieldName: 'impressions',
    label: 'Impressions'
  },
  {
    fieldName: 'clickThroughRate',
    label: 'CTR %'
  },
  {
    fieldName: 'costPerClick',
    label: 'CPC'
  },
  {
    fieldName: 'costPerMilli',
    label: 'CPM'
  },
  {
    fieldName: 'costPerAcquisition',
    label: 'CPA'
  },
  {
    fieldName: 'sales',
    label: 'Ad Sales'
  },
  {
    fieldName: 'conversionRate',
    label: 'Conversion Rate'
  },
  {
    fieldName: 'unitsSold',
    label: 'Ad Units Sold'
  },
  {
    fieldName: 'returnOnAdSpend',
    label: 'ROAS'
  }
];

/**
 * Bar chart for the paid search page
 */
export default function PaidSearchDoubleOptionBarChart() {
  const [localSelectedMetric, setLocalSelectedMetric] = useState('spend');
  const { selectedMetric } = usePaidSearchMetric();
  const effectiveMetric = selectedMetric || localSelectedMetric;

  // TODO: (Topics) Use new indexes when available for topics
  const mainEntityType = useMainEntityType();
  const { isProductEntity } = useProductEntity();
  const isCategoryEntity = mainEntityType === 'category';
  const isSubcategoryEntity = mainEntityType === 'subcategory';
  const titleOnly = !(isProductEntity || isCategoryEntity || isSubcategoryEntity);
  const { convertedBuilderFunc } = useConvertPaidTrafficFieldName();
  const removeRetailPriceRangeFilters = useCreateRemoveRetailPriceRangeFilters();
  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing({ titleOnly });
  const taxonomyText = useTaxonomySubtitleText();

  const groupByFields = useMemo(() => {
    return [
      {
        id: 'searchKeyword',
        label: 'Keyword',
        indexName: 'advertising'
      },
      {
        id: 'campaignId',
        label: 'Campaign',
        indexName: mainEntityType === 'searchtermlist' ? 'advertising' : 'ad-entity-campaign-product-metrics'
      },
      {
        id: 'entityId',
        label: 'Entity',
        indexName: mainEntityType === 'searchtermlist' ? 'advertising' : 'ad-entity-campaign-product-metrics'
      },
      {
        id: 'categoryId',
        label: 'Category',
        indexName: mainEntityType === 'searchtermlist' ? 'advertising' : 'ad-product-metrics'
      },
      {
        id: 'subCategoryId',
        label: 'Subcategory',
        indexName: mainEntityType === 'searchtermlist' ? 'advertising' : 'ad-product-metrics'
      },
      {
        id: 'brandId',
        label: 'Brand',
        indexName: mainEntityType === 'searchtermlist' ? 'advertising' : 'ad-product-metrics'
      },
      {
        id: 'stacklineSku',
        label: 'Product',
        indexName: mainEntityType === 'searchtermlist' ? 'advertising' : 'ad-product-metrics'
      }
    ].filter((option) =>
      mainEntityType === 'product'
        ? ['searchKeyword', 'campaignId', 'entityId'].includes(option.id)
        : !option.id.toLowerCase().startsWith(mainEntityType)
    );
  }, [mainEntityType]);

  const getSubtitle = ({ mainMetricDisplayName, groupByMetricDisplayName }) => {
    const metricName = ['ROAS', 'CPA', 'CPM', 'CPC', 'CTR %'].includes(mainMetricDisplayName)
      ? mainMetricDisplayName
      : mainMetricDisplayName.toLowerCase();
    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`The chart displays the ${metricName} for ${entityPhrase}`}
          {taxonomyText}
          {` in each ${groupByMetricDisplayName.toLowerCase()}`} during the {mainPeriodDisplayName} period compared to
          the {comparisonDisplayName}.
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  return (
    <BeaconDoubleDropdownMetricColumnChart
      defaultSelectedMetric={effectiveMetric}
      selectedField={effectiveMetric}
      onMetricChange={(metric) => setLocalSelectedMetric(metric)}
      beaconChartWithLegendPropsBuilder={
        shouldShowBeaconProTopics()
          ? (props, mainMetric, groupByMetric) => {
              const mainMetricDisplayName = fields.find((field) => field.fieldName === mainMetric)?.label ?? '';
              const groupByMetricDisplayName = groupByFields.find((field) => field.id === groupByMetric)?.label ?? '';

              return {
                ...props,
                includeSummaryInfo: false,
                subtitle: getSubtitle({ mainMetricDisplayName, groupByMetricDisplayName })
              };
            }
          : undefined
      }
      fields={fields}
      groupByFields={groupByFields}
      useAdvancedSearchDataArgs={{
        requestBuilder: (builder) => convertedBuilderFunc(builder.apply(removeRetailPriceRangeFilters()))
      }}
    />
  );
}
